import { useCallback, useEffect, useState } from "react";
import { useChat } from "react-live-chat-loader";

type Result = {
  newChat: (message?: string) => void;
};

type PendingAction = NewChatAction;

type NewChatAction = {
  type: "newChat";
  message: string | undefined;
};

export const useIntercom = (): Result => {
  const [state, loadChat] = useChat({
    loadWhenIdle: true,
  });

  // `loadChat` doesn't return a `Promise`, so the only way to know when Intercom
  // has been loaded is to wait until `state` === 'complete'.
  // That's why we first save a description of the pending action, then load the
  // chat by calling `loadChat` and when `state` === 'complete', we execute that
  // pending action.
  const [pendingAction, setPendingAction] = useState<PendingAction | undefined>();

  useEffect(() => {
    if (state !== "complete" || !pendingAction) {
      return;
    }

    if (pendingAction.type === "newChat") {
      // eslint-disable-next-line new-cap
      window.Intercom?.("showNewMessage", pendingAction.message);
    }

    setPendingAction(undefined);
  }, [state, pendingAction]);

  const newChat = useCallback((message?: string) => {
    loadChat({
      open: false,
    });

    setPendingAction({
      type: "newChat",
      message,
    });
  }, []);

  return { newChat };
};

export const hideIntercom = () => {
  const hide = () => {
    if (window.Intercom) {
      // eslint-disable-next-line new-cap
      window.Intercom?.("update", {
        hide_default_launcher: true,
      });
    } else {
      setTimeout(hide, 100);
    }
  };

  hide();
};

export const showIntercom = () => {
  const show = () => {
    if (window.Intercom) {
      // eslint-disable-next-line new-cap
      window.Intercom?.("update", {
        hide_default_launcher: false,
      });
    } else {
      setTimeout(show, 100);
    }
  };

  show();
};
